import { EditOutlined, ExportOutlined, DownloadOutlined } from "@ant-design/icons";
import { Table, Tooltip, Input, Button } from "antd";
import React, { useCallback, useEffect, useState, useRef } from "react";
import { useTranslation } from 'react-i18next';
import { useSelector } from "react-redux";
import { createPortal } from 'react-dom';
import { getHost } from "../../../../store/history/action";
import { endpoints } from "../../../../services/endpoint.ts/endpoint";
import { URL } from "../../../../utils/contact";
import "./TableHistory.scss";

const TableHistory = ({
  data,
  tableParams,
  onChangePage,
  loading,
  onSelectChange,
  selectedRowKeys,
  handleChangeName,
  totalPages,
  handleReportClick
}) => {
  const { t } = useTranslation();

  const [dataListTable, setDataListTable] = useState([]);
  const [editingReportRow, setEditingReportRow] = useState(null);
  const [reportNames, setReportNames] = useState({});
  
  const [stabilityMsg, setStabilityMsg] = useState("");
  const listHost = useSelector((state) => state.history.data) || [];

  const [isDownloading, setIsDownloading] = useState(false);

  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });
  const [activeHostId, setActiveHostId] = useState(null);
  
  // 팝업 참조 생성
  const popupRef = useRef(null);
  const hostRef = useRef(null);

  useEffect(() => {
    if (data) {
      setDataListTable(data);
    }
  }, [data]);

  const handleMouseEnterStability = useCallback((record) => {
    if (record.stability) {
      const stabilityValue = parseFloat(
        typeof record.stability === "string" ? record.stability.replace("%", "") : record.stability
      );
      if (stabilityValue < 100 && record.stabilityMsg && stabilityMsg !== record.stabilityMsg) {
        setStabilityMsg(record.stabilityMsg);
      }
    }
  }, [stabilityMsg]);

  const handleMouseLeaveStability = () => {
    setStabilityMsg("");
  };

  const renderTooltipContent = (message) => {
    return message.split('\n').map((line, index) => (
      <span key={index}>
        {line}
        <br />
      </span>
    ));
  };

  // 호스트 클릭 핸들러 - 클릭 시 팝업 표시
  const handleHostClick = (e, record) => {
    e.stopPropagation();
    
    // 클릭한 요소의 위치 정보 가져오기
    const rect = e.currentTarget.getBoundingClientRect();
    
    // 팝업 위치 계산 (클릭한 요소 바로 위에 표시)
    setPopupPosition({
      top: rect.top - 10,
      left: rect.left,
      width: Math.max(200, rect.width)
    });
    
    // 활성화된 호스트 ID 설정
    setActiveHostId(record.id);
    
    // 호스트 데이터 가져오기
    getHost({
      host: record.host,
      username: record.username
    });
    
    // 현재 요소 참조 저장
    hostRef.current = e.currentTarget;
  };
  
  // 팝업 외부 클릭 시 닫기
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        activeHostId && 
        popupRef.current && 
        !popupRef.current.contains(event.target) &&
        hostRef.current && 
        !hostRef.current.contains(event.target)
      ) {
        setActiveHostId(null);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeHostId]);

  // 호스트 리스트 항목 클릭 핸들러 - 분할 모드에 표시
  const handleHostItemClick = (id) => {
    // 팝업 닫기
    setActiveHostId(null);
    
    // 선택된 행 키 설정 (분할 모드 표시를 위해)
    handleReportClick(id);
  };

  const openInNewTab = (id) => {
    const width = 1200;
    const height = 800;
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;

    window.open(
      `/summary/${id}`,
      'SummaryPopup',
      `width=${width},height=${height},left=${left},top=${top},menubar=no,toolbar=no,location=no,status=no`
    );
  };

  const handleHostDownload = async (record, e) => {
    e.stopPropagation();
    
    try {
      setIsDownloading(true);
      await new Promise(resolve => setTimeout(resolve, 800));
      
      const queryString = `username=${encodeURIComponent(record.username)}&fileName=${encodeURIComponent(record.name)}`;
      window.location.href = `${URL}${endpoints.GET_FILE_DOWNLOAD.url}?${queryString}`;
      
    } catch (error) {
      console.error('다운로드 실패:', error);
    } finally {
      setTimeout(() => {
        setIsDownloading(false);
      }, 1000);
    }
  };

  const handleReportEditClick = (record) => {
    setEditingReportRow(record.id);
    setReportNames({
      ...reportNames,
      [record.id]: record.name
    });
  };

  const handleReportNameChange = (e, id) => {
    setReportNames({
      ...reportNames,
      [id]: e.target.value,
    });
  };

  const handleReportNameKeyPress = (e, id) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleChangeName({ name: reportNames[id], id: id });
      setEditingReportRow(null);
    }
  };

  const handleReportNameBlur = () => {
    setEditingReportRow(null);
    setReportNames({});
  };
  
  // 포털을 사용한 호스트 팝업 렌더링
  const renderHostPopup = () => {
    if (!activeHostId || !listHost.length) return null;
    
    return createPortal(
      <div 
        ref={popupRef}
        className="host-popup-portal"
        style={{
          position: 'fixed',
          top: `${popupPosition.top}px`,
          left: `${popupPosition.left}px`,
          width: `${popupPosition.width}px`,
          zIndex: 1050,
          background: 'white',
          borderRadius: '4px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
          padding: '10px',
          maxHeight: '300px',
          overflowY: 'auto'
        }}
        onMouseLeave={() => setActiveHostId(null)}
      >
        <div className="host-popup-content">
          {listHost.map((host) => (
            <div
              key={host.id}
              className="host-popup-item"
              onClick={() => handleHostItemClick(host.id)}
              style={{
                padding: '8px',
                cursor: 'pointer',
                borderRadius: '3px',
                marginBottom: '4px',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f0f0f0'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
              title={host.name}
            >
              {host?.name?.length > 25
                ? `${host?.name.slice(0, 25)}...`
                : host?.name}
            </div>
          ))}
        </div>
      </div>,
      document.body
    );
  };

  const columns = [
    {
      title: t('history.col.reportName'),
      dataIndex: "name",
      width: 280,
      render: (text, record) => {
        return (
          <div className="report-name-cell">
            {editingReportRow === record.id ? (
              <Input
                value={reportNames[record.id] || text}
                onChange={(e) => handleReportNameChange(e, record.id)}
                onKeyPress={(e) => handleReportNameKeyPress(e, record.id)}
                onBlur={handleReportNameBlur}
                autoFocus
                maxLength={50}
                placeholder={t('history.tip.reportNamePlaceholder')}
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <div className="report-name-view">
                <div className="report-name-group">
                  <span className="report-name-text">
                    {text}
                  </span>
                  <Tooltip
                    title={t('history.tip.openNewWindow')}
                    overlayClassName="history-tooltip"
                    placement="top"
                    mouseEnterDelay={0.1}
                    mouseLeaveDelay={0.1}
                  >
                    <ExportOutlined 
                      className="new-window-button hover-visible"
                      onClick={(e) => {
                        e.stopPropagation();
                        openInNewTab(record.id);
                      }}
                    />
                  </Tooltip>
                </div>
                <Tooltip title={t('history.tip.renameReport')} overlayClassName="history-tooltip">
                  <Button
                    icon={<EditOutlined />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleReportEditClick(record);
                    }}
                    type="link"
                    className="report-edit-button"
                  />
                </Tooltip>
              </div>
            )}
          </div>
        );
      },
      defaultSortOrder: "name",
      sorter: (a, b) => a.name - b.name,
    },
    {
      title: t('history.col.host'),
      dataIndex: "host",
      render: (text, record) => {
        return (
          <div className="host-cell">
            <div className="host-name-view">
              <span 
                ref={record.id === activeHostId ? hostRef : null}
                className="host-name-text"
                onClick={(e) => handleHostClick(e, record)}
              >
                {text}
              </span>
              <Tooltip 
                title={t('history.tip.downloadFile')} 
                overlayClassName="history-tooltip"
              >
                <Button
                  icon={<DownloadOutlined />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleHostDownload(record, e);
                  }}
                  type="link"
                  className="host-download-button"
                />
              </Tooltip>
            </div>
          </div>
        );
      },
      defaultSortOrder: "host",
      sorter: (a, b) => a.host - b.host,
      width: 210,
    },
    {
      title: "CPU",
      dataIndex: "cpu",
      defaultSortOrder: "cpu",
      sorter: (a, b) => a.cpu - b.cpu,
      align: 'right',
      width: 120,
    },
    {
      title: t('history.col.memory'),
      dataIndex: "memory",
      defaultSortOrder: "memory",
      sorter: (a, b) => a.memory - b.memory,
      align: 'right',
      width: 140,
    },
    {
      title: t('history.col.disk'),
      dataIndex: "diskInfo",
      defaultSortOrder: "diskInfo",
      sorter: (a, b) => a.diskInfo - b.diskInfo,
      align: 'right',
      width: 100,
    },
    {
      title: "OS",
      dataIndex: "os",
      defaultSortOrder: "os",
      sorter: (a, b) => a.os - b.os,
      width: 130,
    },
    {
      title: t('history.col.kernel'),
      dataIndex: "kernel",
      defaultSortOrder: "kernel",
      sorter: (a, b) => a.kernel - b.kernel,
      width: 250,
    },
    {
      title: t('history.col.stability'),
      dataIndex: "stability",
      defaultSortOrder: "stability",
      width: 160,
      render: (value, record) => {
        const stabilityValue = parseFloat(
          typeof value === "string" ? value.replace("%", "") : value || 0
        );
        
        const barStyle = {
          width: `${stabilityValue}%`,
          background:
            stabilityValue >= 90
              ? 'linear-gradient(to right, #0000cc,rgb(32, 143, 235))'
              : stabilityValue >= 80
              ? 'linear-gradient(to right, #0000cc, #99cc33)'
              : stabilityValue >= 70
              ? 'linear-gradient(to right, #0000cc, #ffcc00)'
              : stabilityValue >= 60
              ? 'linear-gradient(to right, #0000cc, #ff9933)'
              : 'linear-gradient(to right, #0000cc, #ff3333)',
          height: '100%',
          borderRadius: '4px',
          transition: 'width 0.3s ease'
        };
        
        const content = stabilityValue < 100 && record.stabilityMsg ? renderTooltipContent(record.stabilityMsg) : null;
        
        return (
          <Tooltip title={content} placement="left" className="stability-tooltip">
            <div 
              className="stability-progress-wrapper"
              onMouseEnter={() => handleMouseEnterStability(record)}
              onMouseLeave={handleMouseLeaveStability}
            >
              <div className="stability-progress-bar">
                <div className="stability-progress-fill" style={barStyle}></div>
                <div className="stability-progress-text-inside">{value}</div>
              </div>
            </div>
          </Tooltip>
        );
      },
      sorter: (a, b) => {
        const stabilityA = parseFloat(
          typeof a.stability === "string" ? a.stability.replace("%", "") : a.stability || 0
        );
        const stabilityB = parseFloat(
          typeof b.stability === "string" ? b.stability.replace("%", "") : b.stability || 0
        );
        return stabilityA - stabilityB;
      },
    }
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  return (
    <div className="wrapper-table">
      <Table
        rowSelection={rowSelection}
        showSorterTooltip={false}
        columns={columns}
        dataSource={dataListTable.map((item) => ({ ...item, key: item.id }))}
        loading={loading || isDownloading}
        pagination={{
          current: tableParams.pageNumber,
          total: totalPages,
          pageSize: tableParams.pageSize,
        }}
        onChange={onChangePage}
        onRow={(record) => ({
          onClick: () => handleReportClick(record.id),
          style: { cursor: 'pointer' }
        })}
      />
      {renderHostPopup()}
    </div>
  );
};

export default TableHistory;